import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/use-auth';
import { useAuthStore } from '../stores/auth.store';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { BrandLogo, BrandWordmark } from '@/components/brand-logo';
import { PasswordInput } from '@/components/auth/password-input';

function formatLoginError(err: unknown): string {
  const msg = err instanceof Error ? err.message : 'Login failed.';
  if (msg.includes('account_locked')) return 'Account temporarily locked. Try again in 15 minutes.';
  if (msg.includes('account_inactive'))
    return 'Your account has been deactivated. Contact your administrator.';
  if (msg.includes('rate_limit')) return 'Too many attempts. Wait a moment before trying again.';
  return 'Incorrect email or password.';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { setAuth } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/home';

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const data = await apiClient<{
        session: { access_token: string; refresh_token: string; user: object };
      }>('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      const session = data.data?.session;
      if (!session) throw new Error('Invalid login response');

      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });

      if (sessionError || !sessionData.session) {
        throw new Error('Failed to establish session.');
      }

      return sessionData.session;
    },
    onSuccess: (session) => {
      setAuth(session.user, session);
      navigate(from, { replace: true });
    },
  });

  if (!isLoading && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  }

  const error = loginMutation.error ? formatLoginError(loginMutation.error) : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="flex w-full max-w-md flex-col">
        <div className="rounded-xl glass shadow-soft p-8">
          <div className="flex flex-col items-center mb-6">
            <BrandLogo className="mb-3 h-16 w-16" />
            <h1>
              <BrandWordmark align="center" className="text-[24px]" />
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your account</p>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-input px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring bg-background"
              />
            </div>

            <PasswordInput
              id="password"
              label="Password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <div className="flex items-center justify-end">
              <a href="/reset-password" className="text-sm text-primary hover:underline">
                Forgot password?
              </a>
            </div>

            <Button type="submit" disabled={loginMutation.isPending} className="w-full" size="lg">
              {loginMutation.isPending ? 'Signing in\u2026' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
